import { useState } from "react";
import { Link } from "react-router-dom";
import type { Chapter } from "@/types/comic";

interface ChapterListProps {
  comicId: string;
  chapters: Chapter[];
  lastReadId?: string;
}

export default function ChapterList({
  comicId,
  chapters,
  lastReadId,
}: ChapterListProps) {
  const [desc, setDesc] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const sorted = [...chapters].sort((a, b) =>
    desc ? b.index - a.index : a.index - b.index,
  );
  const shown = expanded ? sorted : sorted.slice(0, 12);

  return (
    <section className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-950">
          章节目录
          <span className="ml-2 text-sm font-normal text-foreground-500">
            共 {chapters.length} 话
          </span>
        </h2>
        <button
          type="button"
          onClick={() => setDesc((prev) => !prev)}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-background-300 text-xs md:text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-sort-asc text-base"></i>
          {desc ? "倒序排列（最新在前）" : "正序排列（最早在前）"}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
        {shown.map((chapter) => {
          const isLast = chapter.id === lastReadId;
          return (
            <Link
              key={chapter.id}
              to={`/read/${comicId}/${chapter.id}`}
              className={`flex flex-col gap-1 px-3 py-2.5 rounded-md border text-left transition-colors ${
                isLast
                  ? "border-accent-400 bg-accent-50"
                  : "border-background-200 bg-background-50 hover:border-accent-300"
              }`}
            >
              <span className="text-sm text-foreground-900 font-medium line-clamp-1">
                {chapter.title}
              </span>
              <span className="flex items-center gap-1 text-[11px] text-foreground-400">
                <i className="ri-calendar-line"></i>
                {chapter.date}
                {isLast && (
                  <span className="ml-auto text-accent-600 font-medium">
                    读到这
                  </span>
                )}
              </span>
            </Link>
          );
        })}
      </div>

      {sorted.length > 12 && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-5 w-full h-10 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
        >
          {expanded ? "收起章节" : `展开全部 ${sorted.length} 话`}
        </button>
      )}
    </section>
  );
}